import classNames from 'classnames';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath, useMatch } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import ButtonBase from 'atoms/buttons/ButtonBase';
import { LinkBox, LinkOverlay } from 'atoms/navigation/Box';
import { LabelL } from 'atoms/typography';
import { Chip } from 'atoms/ui/Chip';
import { Reward } from 'components/rewards/types';
import { MY_CLUB_HISTORY_BY_SPORT } from 'constants/routes';
import { useCurrentUserContext } from 'contexts/currentUser';
import useCurrentSport from 'hooks/useCurrentSport';
import useToggle from 'hooks/useToggle';

import { ClaimRewardsDialog } from '../ClaimRewardsDialog';
import { Fan } from './Fan';
import { RainbowCircle } from './RainbowCircle';

const Banner = styled(LinkBox)`
  --fan-wrapper-width: 100%;
  --fan-element-width: 16px;
  display: grid;
  grid-template-areas:
    'icon title'
    'icon cta';
  align-items: center;
  column-gap: var(--intermediate-unit);
  &.withMargin {
    margin: var(--triple-unit) 0;
  }
`;

const RainbowWrapper = styled.div`
  grid-area: icon;
  width: calc(8 * var(--unit));
`;

export type RewardsBannerProps = {
  rewards: Reward[];
  onClaim: (id: string[]) => void;
  onCloseClaimRewardsDialog?: () => void;
  noMargin?: boolean;
  supportPartialClaim?: boolean;
  disabledClaim?: boolean;
  setSafeGoNextReward?: (cb: () => void) => void;
  renderBanner?: (args: { toggleShowClaimReward: () => void }) => ReactNode;
};

export const RewardsBanner = ({
  rewards,
  onClaim,
  noMargin,
  supportPartialClaim = false,
  disabledClaim,
  setSafeGoNextReward,
  renderBanner,
  onCloseClaimRewardsDialog,
}: RewardsBannerProps) => {
  const unclaimedRewards = rewards.filter(r => !r.claimed);
  const [showClaimReward, toggleShowClaimReward] = useToggle(false);
  const noUnclaimedRewards = unclaimedRewards.length === 0;
  const { currentUser } = useCurrentUserContext();
  const currentSport = useCurrentSport();
  const isInMyClubHistory = !!useMatch(MY_CLUB_HISTORY_BY_SPORT[currentSport]);

  if (!currentUser) {
    return null;
  }
  // prevents closing the claim modal too early
  if (rewards.length > 0) {
    const claimRewardsDialog = (
      <ClaimRewardsDialog
        open={!disabledClaim && showClaimReward}
        onClose={() => {
          toggleShowClaimReward();
          onCloseClaimRewardsDialog?.();
        }}
        rewards={rewards}
        onClaim={onClaim}
        setSafeGoNextReward={setSafeGoNextReward}
      />
    );
    if (renderBanner) {
      return (
        <>
          {renderBanner({
            toggleShowClaimReward,
          })}
          {claimRewardsDialog}
        </>
      );
    }

    return (
      <>
        <Banner
          className={classNames({
            withMargin: !noMargin,
          })}
        >
          <RainbowWrapper>
            <RainbowCircle>
              <Fan
                elements={(supportPartialClaim
                  ? unclaimedRewards
                  : rewards
                ).map(({ back }) => back)}
              />
            </RainbowCircle>
          </RainbowWrapper>
          <LabelL as="h5" className="text-center">
            {noUnclaimedRewards ? (
              <FormattedMessage
                id="RewardsBanner.claimedMessage"
                defaultMessage="{nb, plural, one {Reward claimed} other {Rewards claimed}}"
                values={{
                  nb: (supportPartialClaim ? unclaimedRewards : rewards).length,
                }}
              />
            ) : (
              <FormattedMessage
                id="RewardsBanner.message"
                defaultMessage="You've earned {nb, plural, one {1 reward} other {# rewards}}"
                values={{
                  nb: (supportPartialClaim ? unclaimedRewards : rewards).length,
                }}
              />
            )}
          </LabelL>
          <div>
            {noUnclaimedRewards ? (
              !isInMyClubHistory && (
                <LinkOverlay
                  as={Link}
                  to={generatePath(MY_CLUB_HISTORY_BY_SPORT[currentSport], {
                    slug: currentUser.slug,
                  })}
                >
                  <Chip>
                    <FormattedMessage
                      id="RewardsBanner.viewProResults"
                      defaultMessage="View results"
                    />
                  </Chip>
                </LinkOverlay>
              )
            ) : (
              <LinkOverlay
                as={ButtonBase}
                onClick={!disabledClaim ? toggleShowClaimReward : undefined}
              >
                <Chip>
                  <FormattedMessage
                    id="RewardsBanner.claimCta"
                    defaultMessage="Claim {count, plural, one {reward} other {rewards}}"
                    values={{
                      count: unclaimedRewards.length,
                    }}
                  />
                </Chip>
              </LinkOverlay>
            )}
          </div>
        </Banner>
        {claimRewardsDialog}
      </>
    );
  }
  return null;
};
